import { MedicalIcon } from "@/components/flutter-asset/medical-icon";
import { useI18n } from "@/utils/i18n";
import { SATError } from "@sadtx/sat-error";
import { useEffect, useState } from "react";
import styles from "./custom-toast.module.css";

export enum CustomToastType {
  WARNING = "warning",
  SUCCESS = "success",
}

type Toast = {
  id: string;
  type: CustomToastType;
  message: string;
};

let toastList: Toast[] = [];

function emitToast() {
  window.dispatchEvent(new CustomEvent("sat014-toast"));
}

function removeToast(id: string) {
  toastList = toastList.filter((toast) => toast.id !== id);
  emitToast();
}

export function addWarningToast(error: SATError | Error | string) {
  toastList = [
    ...toastList,
    {
      id: String(Date.now()),
      type: CustomToastType.WARNING,
      message: typeof error === "string" ? error : error.message,
    },
  ];
  emitToast();
}

export function addSuccessToast(message: string) {
  toastList = [
    ...toastList,
    {
      id: String(Date.now()),
      type: CustomToastType.SUCCESS,
      message,
    },
  ];
  emitToast();
}

export default function CustomToast() {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [closingIds, setClosingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const sync = () => setToasts([...toastList]);
    sync();
    window.addEventListener("sat014-toast", sync);
    return () => window.removeEventListener("sat014-toast", sync);
  }, []);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timer = window.setTimeout(() => {
      startClosing(toasts[0].id);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [toasts]);

  const startClosing = (id: string) => {
    setClosingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    window.setTimeout(() => {
      removeToast(id);
      setClosingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  };

  return (
    <div
      className={`${styles.layer} ${toasts.length === 0 ? styles.hidden : ""}`}
      aria-live="polite"
    >
      {toasts
        .slice()
        .reverse()
        .map((toast) => (
          <div
            key={toast.id}
            className={`${styles.toast} ${styles[toast.type]} ${
              closingIds.has(toast.id) ? styles.closing : ""
            }`}
          >
            <MedicalIcon
              className={styles.icon}
              name={toast.type === "success" ? "check" : "memo"}
            />
            <span className={styles.message}>{toast.message}</span>
            <button
              className={styles.close}
              type="button"
              aria-label={t("toast.closeNotification")}
              onClick={() => startClosing(toast.id)}
            >
              <MedicalIcon className={styles.closeIcon} name="cancel" />
            </button>
          </div>
        ))}
    </div>
  );
}
